/**
 * SupplierBookingDetailModal ✦
 *
 * Detail view for a settled supplier booking. Opens from the
 * Supplier Bookings ledger or any other surface where the supplier
 * wants to see — and quietly adjust — a single booking.
 *
 * Stages:
 *   view   — calm read-only summary + edit ✦ + delete ✦ + share actions
 *   edit   — same fields exposed inline for inline correction
 *   delete — confirm step before the irreversible wipe
 *
 * Out of scope (deferred phase): payments, e-signatures, calendar
 * sync, audit log. The supplier can resend the confirmation if a
 * material term changed; the resend keeps using the existing email
 * template + .ics attachment.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Copy, CheckCircle, X, EnvelopeSimple, PencilSimple, Trash,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONFIRMATION_URL = (token) => {
  if (typeof window === 'undefined') return `/book/confirmation/${token}`;
  return `${window.location.origin}/book/confirmation/${token}`;
};

const SHELL_BG = '#15161B';
const RULE = 'rgba(194,157,82,0.22)';
const CREAM = '#F4E9D2';
const CREAM_MUTED = 'rgba(244,233,210,0.62)';
const GOLD = '#C29D52';
const DANGER = '#E07A5F';

const labelStyle = {
  fontFamily: 'Arial, sans-serif',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: GOLD,
  marginBottom: 6,
  display: 'block',
};

const inputStyle = {
  width: '100%',
  padding: '11px 13px',
  background: '#1B1C22',
  border: `1px solid ${RULE}`,
  borderRadius: 4,
  color: CREAM,
  fontFamily: 'Georgia, serif',
  fontSize: 14.5,
  outline: 'none',
};

const Row = ({ label, value }) => {
  if (!value) return null;
  return (
    <div style={{ marginTop: 16 }}>
      <p style={labelStyle}>{label}</p>
      <p style={{
        margin: '2px 0 0', fontFamily: 'Georgia, serif', fontSize: 14.5,
        color: CREAM, lineHeight: 1.5,
      }}>{value}</p>
    </div>
  );
};

const SupplierBookingDetailModal = ({ open, linkId, onClose, onChanged, onDeleted }) => {
  const [stage, setStage] = useState('view'); // view | edit | delete
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resentAt, setResentAt] = useState(null);

  // Load detail when the modal opens (and whenever linkId changes).
  useEffect(() => {
    if (!open || !linkId) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setError(''); setStage('view'); setCopied(false); setResentAt(null);
      try {
        const r = await axios.get(`${API}/studio/bookings/${linkId}`);
        if (cancelled) return;
        setDetail(r.data);
        const bl = r.data.booking_link || {};
        setDraft({
          client_name: bl.client_name || '',
          client_email: bl.client_email || '',
          client_mobile: bl.client_mobile || bl.client_phone || '',
          event_date: bl.event_date || '',
          event_time: bl.event_time || '',
          event_type: bl.event_type || '',
          venue: bl.venue || '',
          venue_address: bl.venue_address || '',
          access_issues: bl.access_issues || '',
          package_name: bl.package_name || '',
          supplier_note: bl.supplier_note || '',
          terms_text: bl.terms_text || '',
        });
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.detail || 'Could not load booking detail');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, linkId]);

  if (!open) return null;

  const bl = detail?.booking_link || {};
  const settled = bl.status === 'linked' || bl.status === 'accepted';
  const originLabel = bl.booking_origin === 'supplier' ? 'Created quietly' : 'Requested';

  const saveEdits = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.patch(`${API}/studio/bookings/${linkId}`, draft);
      // Refresh detail from server so we render the canonical state.
      const fresh = await axios.get(`${API}/studio/bookings/${linkId}`);
      setDetail(fresh.data);
      setStage('view');
      if (onChanged) onChanged(r.data?.booking_link);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not save changes');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setBusy(true); setError('');
    try {
      await axios.delete(`${API}/studio/bookings/${linkId}`);
      if (onDeleted) onDeleted(linkId);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not delete booking');
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!bl?.token) return;
    try {
      await navigator.clipboard.writeText(CONFIRMATION_URL(bl.token));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked — fall through */
    }
  };

  const resendEmail = async () => {
    if (!linkId) return;
    setBusy(true); setError('');
    try {
      await axios.post(`${API}/studio/booking-links/${linkId}/resend-confirmation`);
      setResentAt(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not resend confirmation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="supplier-booking-detail-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        background: 'rgba(12,8,4,0.78)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 600, background: SHELL_BG,
          border: `1px solid ${RULE}`, borderRadius: 8,
          color: CREAM, fontFamily: 'Georgia, serif',
          maxHeight: '94vh', overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{ padding: '24px 28px 4px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ width: 42, height: 1, background: GOLD, marginBottom: 14 }} />
            <p style={labelStyle}>
              ✦ {stage === 'edit' ? 'Update booking' : stage === 'delete' ? 'Delete booking' : 'Booking detail'}
            </p>
            <h2 style={{
              margin: '8px 0 0', fontSize: 22, fontWeight: 500,
              letterSpacing: '-0.012em', lineHeight: 1.25,
            }}>
              {bl.client_name || (loading ? 'Opening…' : 'A booking')}
            </h2>
            {settled && (
              <p style={{
                margin: '6px 0 0', fontFamily: 'Arial, sans-serif', fontSize: 10,
                fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase',
                color: 'rgba(194,157,82,0.7)',
              }}>
                {originLabel}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="detail-close"
            style={{ background: 'transparent', border: 'none', color: CREAM_MUTED, cursor: 'pointer', marginTop: 4 }}
            aria-label="Close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {loading && (
          <p style={{ padding: '20px 28px', color: CREAM_MUTED, fontStyle: 'italic' }}>
            Opening the register…
          </p>
        )}

        {error && stage !== 'delete' && (
          <p data-testid="detail-error" style={{ padding: '0 28px', color: DANGER, fontSize: 13 }}>
            {error}
          </p>
        )}

        {/* VIEW MODE — read-only summary */}
        {stage === 'view' && !loading && bl && (
          <div style={{ padding: '12px 28px 28px' }}>
            <Row label="Package" value={bl.package_name} />
            <Row label="Event date" value={bl.event_date} />
            <Row label="Time" value={bl.event_time} />
            <Row label="Event type" value={bl.event_type} />
            <Row label="Venue" value={bl.venue} />
            <Row label="Venue address" value={bl.venue_address} />
            <Row label="Access notes" value={bl.access_issues} />
            <Row label="Client" value={bl.client_name} />
            <Row label="Client email" value={bl.client_email} />
            <Row label="Client mobile" value={bl.client_mobile || bl.client_phone} />
            <Row label="Supplier note" value={bl.supplier_note} />

            {/* Share actions */}
            {settled && bl.token && (
              <div style={{
                marginTop: 24, paddingTop: 18,
                borderTop: `1px solid ${RULE}`,
                display: 'flex', flexWrap: 'wrap', gap: 8,
              }}>
                <button
                  onClick={copyLink}
                  data-testid="detail-copy-confirmation"
                  style={{
                    padding: '9px 14px', background: 'transparent', color: CREAM,
                    border: `1px solid ${RULE}`, borderRadius: 3,
                    fontFamily: 'Georgia, serif', fontSize: 13,
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7,
                  }}
                >
                  {copied ? <CheckCircle size={13} weight="fill" /> : <Copy size={13} weight="bold" />}
                  {copied ? 'Copied ✦' : 'Copy confirmation link'}
                </button>
                <button
                  onClick={resendEmail}
                  disabled={busy}
                  data-testid="detail-resend"
                  style={{
                    padding: '9px 14px', background: 'transparent', color: CREAM,
                    border: `1px solid ${RULE}`, borderRadius: 3,
                    fontFamily: 'Georgia, serif', fontSize: 13,
                    cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                  }}
                >
                  <EnvelopeSimple size={13} weight="bold" />
                  {resentAt ? `Resent · ${resentAt}` : 'Resend by email'}
                </button>
                <a
                  href={`${API}/book/${bl.token}/confirmation.pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detail-download-pdf"
                  style={{
                    padding: '9px 14px', background: 'transparent', color: CREAM,
                    border: `1px solid ${RULE}`, borderRadius: 3,
                    fontFamily: 'Georgia, serif', fontSize: 13,
                    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 7,
                  }}
                >
                  Download PDF
                </a>
              </div>
            )}

            {/* Mutate actions */}
            <div style={{
              marginTop: 24, paddingTop: 18,
              borderTop: `1px solid ${RULE}`,
              display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
            }}>
              <button
                onClick={() => setStage('edit')}
                data-testid="detail-edit"
                style={{
                  padding: '10px 18px', background: GOLD, color: '#1A1208',
                  border: 'none', borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 14,
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7,
                }}
              >
                <PencilSimple size={13} weight="bold" />
                Edit booking ✦
              </button>
              <button
                onClick={() => setStage('delete')}
                data-testid="detail-delete"
                style={{
                  padding: '10px 16px', background: 'transparent', color: DANGER,
                  border: `1px solid rgba(224,122,95,0.45)`, borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontSize: 13,
                  cursor: 'pointer', marginLeft: 'auto',
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                }}
              >
                <Trash size={13} weight="bold" />
                Delete
              </button>
            </div>
          </div>
        )}

        {/* EDIT MODE — same fields, editable */}
        {stage === 'edit' && (
          <div style={{ padding: '12px 28px 28px' }}>
            <p style={{ color: CREAM_MUTED, fontSize: 13, lineHeight: 1.6, marginTop: 0, marginBottom: 18, fontStyle: 'italic' }}>
              Update what changed. The supplier diary mirrors automatically.
              If a material term shifted, resend the confirmation from the View tab.
            </p>
            <div style={{ display: 'grid', gap: 13, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <div>
                <label style={labelStyle}>Client name</label>
                <input data-testid="edit-client-name" style={inputStyle}
                  value={draft.client_name} onChange={(e) => setDraft({ ...draft, client_name: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Client email</label>
                <input data-testid="edit-client-email" type="email" style={inputStyle}
                  value={draft.client_email} onChange={(e) => setDraft({ ...draft, client_email: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Client mobile</label>
                <input data-testid="edit-client-mobile" style={inputStyle}
                  value={draft.client_mobile} onChange={(e) => setDraft({ ...draft, client_mobile: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Event date</label>
                <input data-testid="edit-event-date" type="date" style={inputStyle}
                  value={draft.event_date} onChange={(e) => setDraft({ ...draft, event_date: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Time</label>
                <input data-testid="edit-event-time" style={inputStyle}
                  value={draft.event_time} onChange={(e) => setDraft({ ...draft, event_time: e.target.value })} />
              </div>
              <div>
                <label style={labelStyle}>Event type</label>
                <input data-testid="edit-event-type" style={inputStyle}
                  value={draft.event_type} onChange={(e) => setDraft({ ...draft, event_type: e.target.value })} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Venue</label>
                <input data-testid="edit-venue" style={inputStyle}
                  value={draft.venue} onChange={(e) => setDraft({ ...draft, venue: e.target.value })} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Venue address</label>
                <input data-testid="edit-venue-address" style={inputStyle}
                  value={draft.venue_address} onChange={(e) => setDraft({ ...draft, venue_address: e.target.value })} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Access notes</label>
                <input data-testid="edit-access-issues" style={inputStyle}
                  value={draft.access_issues} onChange={(e) => setDraft({ ...draft, access_issues: e.target.value })} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Package</label>
                <input data-testid="edit-package" style={inputStyle}
                  value={draft.package_name} onChange={(e) => setDraft({ ...draft, package_name: e.target.value })} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Supplier note</label>
                <textarea data-testid="edit-supplier-note" rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                  value={draft.supplier_note} onChange={(e) => setDraft({ ...draft, supplier_note: e.target.value })} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Terms & conditions</label>
                <textarea data-testid="edit-terms-text" rows={4} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.55 }}
                  value={draft.terms_text} onChange={(e) => setDraft({ ...draft, terms_text: e.target.value })} />
              </div>
            </div>
            <div style={{ marginTop: 22, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={saveEdits}
                disabled={busy}
                data-testid="edit-save"
                style={{
                  padding: '11px 22px', background: GOLD, color: '#1A1208',
                  border: 'none', borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 14,
                  cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
                }}
              >
                {busy ? 'Saving…' : 'Save changes ✦'}
              </button>
              <button
                onClick={() => setStage('view')}
                data-testid="edit-cancel"
                style={{
                  padding: '11px 20px', background: 'transparent', color: CREAM_MUTED,
                  border: `1px solid ${RULE}`, borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontSize: 13.5, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* DELETE CONFIRM */}
        {stage === 'delete' && (
          <div style={{ padding: '12px 28px 28px' }}>
            <p style={{ color: CREAM, fontSize: 15, lineHeight: 1.65, marginTop: 0 }}>
              This will remove the booking record, the diary row, and the linked
              EventDrop event if it was created here. The action can't be undone.
            </p>
            {error && (
              <p data-testid="delete-error" style={{ color: DANGER, fontSize: 13, marginTop: 14 }}>{error}</p>
            )}
            <div style={{ marginTop: 22, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={doDelete}
                disabled={busy}
                data-testid="delete-confirm"
                style={{
                  padding: '11px 22px', background: DANGER, color: '#1A1208',
                  border: 'none', borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 14,
                  cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                }}
              >
                <Trash size={13} weight="bold" />
                {busy ? 'Removing…' : 'Yes, delete this booking'}
              </button>
              <button
                onClick={() => { setStage('view'); setError(''); }}
                data-testid="delete-cancel"
                style={{
                  padding: '11px 20px', background: 'transparent', color: CREAM_MUTED,
                  border: `1px solid ${RULE}`, borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontSize: 13.5, cursor: 'pointer',
                }}
              >
                Keep it
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierBookingDetailModal;
